import { useTranslation } from "@hireroo/i18n";
import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import AppBar from "@mui/material/AppBar";
import Button, { ButtonProps } from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import * as React from "react";

import BrandLogo from "../../modules/BrandLogo/BrandLogo";
import HelpMenu, { HelpMenuProps } from "../../modules/HelpMenu/HelpMenu";
import LanguageDropDownMenu from "../../modules/LanguageDropDownMenu/LanguageDropDownMenu";
import PreferencesMenu, { PreferencesMenuProps } from "../../modules/PreferencesMenu/PreferencesMenu";
import SwitchInterfaceField, { SwitchInterfaceFieldProps } from "./parts/SwitchInterfaceField/SwitchInterfaceField";

const HEIGHT = 48;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  width: "100%",
  height: HEIGHT,
  border: "none",
  borderBottom: `solid 1px ${theme.palette["Secondary/Shades"].p16}`,
  boxShadow: "none",
  flexGrow: 1,
  flexShrink: 0,
}));

const StyledStack = styled(Stack)(() => ({
  flexGrow: 1,
  flexShrink: 0,
  height: HEIGHT,
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  overflow: "hidden",
}));

const StyledButton = styled(Button)`
  word-break: keep-all;
  white-space: nowrap;
  overflow: hidden;
`;

export type RemoteInterviewCandidateHeaderProps = {
  Participants: React.ReactNode;
  toggleVideoButton?: { type: "ENTER" | "EXIT"; onClick: ButtonProps["onClick"] };
  PreJoinScreen: React.ReactNode;
  helpMenu: HelpMenuProps;
  item: { type: "MANY"; content: PreferencesMenuProps } | { type: "BOTH"; switchInterfaceField?: SwitchInterfaceFieldProps };
};

const RemoteInterviewCandidateHeader: React.FC<RemoteInterviewCandidateHeaderProps> = props => {
  const { t } = useTranslation();

  const setupVideoButtonProps = React.useMemo(
    (): ButtonProps => ({
      startIcon: <VideocamOutlinedIcon />,
      color: "secondary",
      variant: "outlined",
      children: t("ビデオ通話"),
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        props.toggleVideoButton?.onClick?.(event);
      },
    }),
    [props.toggleVideoButton, t],
  );

  const endVideoButtonProps = React.useMemo(
    (): ButtonProps => ({
      startIcon: <CallEndOutlinedIcon />,
      color: "error",
      variant: "contained",
      children: t("ビデオ通話から退出"),
      onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
        props.toggleVideoButton?.onClick?.(event);
      },
    }),
    [props.toggleVideoButton, t],
  );

  const toggleVideoButtonProps = React.useMemo((): ButtonProps => {
    const buttonPropsMap: Record<string, ButtonProps> = {
      EXIT: setupVideoButtonProps,
      ENTER: endVideoButtonProps,
    };
    return buttonPropsMap[props.toggleVideoButton?.type ?? "EXIT"];
  }, [endVideoButtonProps, props.toggleVideoButton?.type, setupVideoButtonProps]);

  return (
    <StyledAppBar>
      <StyledStack direction="row" px={1.5} py={0.5}>
        <BrandLogo />
        <Stack direction="row" spacing={2} alignItems="center">
          {props.toggleVideoButton && <StyledButton {...toggleVideoButtonProps} onClick={props.toggleVideoButton.onClick} />}
          {props.PreJoinScreen}
          {props.item.type === "BOTH" && (
            <>
              {props.item.switchInterfaceField && <SwitchInterfaceField {...props.item.switchInterfaceField} />}
              <LanguageDropDownMenu />
            </>
          )}
          <HelpMenu {...props.helpMenu} />
          {props.item.type === "MANY" && <PreferencesMenu {...props.item.content} />}
          {props.Participants}
        </Stack>
      </StyledStack>
    </StyledAppBar>
  );
};

RemoteInterviewCandidateHeader.displayName = "RemoteInterviewCandidateHeader";

export default RemoteInterviewCandidateHeader;
